import React, {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
} from 'react';
import { Overlay, Popover } from 'react-bootstrap';

import TooltipWrapper from 'components/DesignSystem/TooltipWrapper';
import usePopoverDismiss, {
  PERIOD_POPOVER_OFFSET,
  PopoverDismissReason,
} from 'components/Flux/Personalized/MyStudies/FilterBar/PeriodFilter/usePopoverDismiss';
import { MyStudiesDateRef } from 'types/Flux/Personalized/myStudiesFilters';

/** Nome de cada referência, na pílula e no menu (.dc.html:668-669). */
export const DATE_REF_LABELS: Readonly<Record<MyStudiesDateRef, string>> = {
  last_exec: 'Última execução',
  created: 'Criação do estudo',
};

/** Opções do menu, na ordem do protótipo, com as descrições exatas (.dc.html:669). */
const DATE_REF_OPTIONS: ReadonlyArray<{ value: MyStudiesDateRef; description: string }> = [
  { value: 'last_exec', description: 'Padrão. Inclui estudos antigos que foram reenviados no período.' },
  { value: 'created', description: 'Só estudos criados no período, mesmo que reenviados depois.' },
];

/**
 * Dica de "Criação do estudo" enquanto a referência não é liberada (D8). Não afirma nada sobre o
 * backend: só que a filtragem ainda não foi liberada.
 */
export const CREATED_REF_UNAVAILABLE_HINT = (
  'Indisponível por enquanto: a filtragem pela data de criação ainda não foi liberada.'
);

/**
 * Por que o menu fechou sem escolher: Esc, clique fora ou o foco saiu dele (Tab e Shift+Tab levam
 * o foco ao campo seguinte ou à pílula).
 */
export type ReferenceMenuCloseReason = PopoverDismissReason | 'blur';

/** Teclas que movem o foco entre as opções. */
const NAVIGATION_KEYS = ['ArrowDown', 'ArrowUp', 'Home', 'End'] as const;
type NavigationKey = typeof NAVIGATION_KEYS[number];

const isNavigationKey = (key: string): key is NavigationKey => (
  (NAVIGATION_KEYS as readonly string[]).includes(key)
);

export interface ReferencePopoverProps {
  /** Menu aberto. */
  show: boolean;
  /** Onde o menu abre: embaixo, alinhado à esquerda (a linha do rótulo, .dc.html:175-179). */
  target: RefObject<HTMLElement | null>;
  /**
   * Onde o menu é montado no DOM (padrão: o `<body>`). O `PeriodFilter` passa um elemento logo
   * depois da pílula: assim o Tab sai do menu para o campo seguinte, e o Shift+Tab, para a pílula.
   */
  container?: RefObject<HTMLElement | null>;
  /** `id` do menu, par do `aria-controls` da pílula. */
  id: string;
  /** Referência aplicada (`filters.ref`). */
  value: MyStudiesDateRef;
  /**
   * "Criação do estudo" escolhível. Enquanto for `false`, a opção fica `aria-disabled`, com dica
   * no hover e no foco (D8). A #2006 liga quando o backend confirmar o `created_at`.
   */
  createdRefAvailable: boolean;
  /** Recebe a referência escolhida (só de uma opção habilitada). */
  onSelect: (value: MyStudiesDateRef) => void;
  /** Pede para fechar sem escolher. */
  onClose: (reason: ReferenceMenuCloseReason) => void;
}

type ReferenceOptionsProps = Pick<ReferencePopoverProps, 'value' | 'createdRefAvailable' | 'onSelect'>;

/**
 * Opções do menu: só existem com ele aberto, então montar é abrir. Ao montar, o foco vai para a
 * referência aplicada (o conteúdo do `Overlay` pode montar depois do próprio `Overlay`, quando o
 * `container` fica pronto).
 */
function ReferenceOptions({ value, createdRefAvailable, onSelect }: ReferenceOptionsProps) {
  const baseId = useId();
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    const index = DATE_REF_OPTIONS.findIndex((option) => option.value === value);
    itemRefs.current[Math.max(index, 0)]?.focus({ preventScroll: true });
  }, [value]);

  const handleItemKeyDown = useCallback((
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (!isNavigationKey(event.key)) return;
    event.preventDefault();
    const last = DATE_REF_OPTIONS.length - 1;
    const next: Record<NavigationKey, number> = {
      ArrowDown: index >= last ? 0 : index + 1,
      ArrowUp: index <= 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    itemRefs.current[next[event.key]]?.focus();
  }, []);

  return (
    <>
      {DATE_REF_OPTIONS.map((option, index): ReactNode => {
        const disabled = option.value === 'created' && !createdRefAvailable;
        const checked = option.value === value;
        const labelId = `${baseId}-${option.value}-label`;
        const descriptionId = `${baseId}-${option.value}-description`;
        const hintId = `${baseId}-${option.value}-hint`;

        const item = (
          <button
            ref={(element) => { itemRefs.current[index] = element; }}
            type="button"
            role="menuitemradio"
            // Fora da ordem de Tab: o foco anda pelas setas, como num menu
            tabIndex={-1}
            aria-checked={checked}
            aria-disabled={disabled || undefined}
            aria-labelledby={labelId}
            aria-describedby={disabled ? `${descriptionId} ${hintId}` : descriptionId}
            className={`my-studies-reference-option${checked ? ' is-selected' : ''}`}
            onKeyDown={(event) => handleItemKeyDown(event, index)}
            onClick={() => {
              if (!disabled) onSelect(option.value);
            }}
          >
            <span id={labelId} className="my-studies-reference-option-label">
              {DATE_REF_LABELS[option.value]}
            </span>
            <span id={descriptionId} className="my-studies-reference-option-description">
              {option.description}
            </span>
          </button>
        );

        if (!disabled) return <React.Fragment key={option.value}>{item}</React.Fragment>;
        // Desabilitada: a dica aparece no hover e no foco, e o leitor de tela a lê na descrição (o
        // `hidden` tira o texto da navegação, mas não do `aria-describedby`)
        return (
          <TooltipWrapper
            key={option.value}
            title={CREATED_REF_UNAVAILABLE_HINT}
            placement="right"
            className="my-studies-reference-option-hint"
          >
            {item}
            <span id={hintId} hidden>{CREATED_REF_UNAVAILABLE_HINT}</span>
          </TooltipWrapper>
        );
      })}
    </>
  );
}

/**
 * Menu da referência do período (#1996): "Última execução" ou "Criação do estudo", cada uma com a
 * sua descrição (.dc.html:178-187). Abre pela pílula ao lado do rótulo "PERÍODO · REFERENTE À",
 * controlado pelo `PeriodFilter`, que aplica a escolha e devolve o foco à pílula.
 *
 * Padrão de menu do APG (`menuitemradio`): abre com o foco na referência aplicada; setas (em
 * volta), Home e End movem o foco; Enter, Espaço ou clique escolhem; Esc e clique fora fecham sem
 * escolher; o foco sair do menu (Tab, Shift+Tab) também fecha. O foco não fica preso: num menu, o
 * Tab segue a ordem da página. A opção desabilitada continua na navegação: é ao receber o foco que
 * a dica aparece.
 */
export default function ReferencePopover({
  show,
  target,
  container,
  id,
  value,
  createdRefAvailable,
  onSelect,
  onClose,
}: ReferencePopoverProps) {
  const handleHide = usePopoverDismiss(show, onClose);

  // O foco saiu do menu (Tab, Shift+Tab): fecha depois de o foco chegar ao destino. Fechar no
  // `keydown` do Tab desmontaria o item focado antes de o navegador mover o foco, que cairia no
  // <body>. Sem `relatedTarget` (clique numa área sem foco), quem fecha é o clique fora.
  const handleBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (!(next instanceof Node) || event.currentTarget.contains(next)) return;
    onClose('blur');
  }, [onClose]);

  // O mousedown fora das opções (padding do menu, folga da dica) não tira o foco da opção: ele
  // cairia no <body>, onde as setas não alcançam o menu
  const keepOptionFocus = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const pressed = event.target;
    if (pressed instanceof Element && pressed.closest('button')) return;
    event.preventDefault();
  }, []);

  return (
    <Overlay
      show={show}
      target={target}
      container={container}
      placement="bottom-start"
      offset={PERIOD_POPOVER_OFFSET}
      flip
      rootClose
      onHide={handleHide}
      transition={false}
    >
      <Popover
        id={id}
        role="menu"
        aria-label="Referência do período"
        className="my-studies-period-popover my-studies-reference-menu"
        onBlur={handleBlur}
        onMouseDown={keepOptionFocus}
      >
        <ReferenceOptions
          value={value}
          createdRefAvailable={createdRefAvailable}
          onSelect={onSelect}
        />
      </Popover>
    </Overlay>
  );
}
